import Button from '@/components/base/button/button';
import Divider from '@/components/base/divider/divider';
import PhosphorIcon from '@/components/base/icon/phosphor-icon';
import { MobileNavigationLink } from '@/features/navigation/components/mobile-menu/mobile-navigation-link';
import {
  NavigationStore,
  useNavigationStore,
} from '@/features/navigation/store/navigation-store';
import type { Navigation } from '@/features/navigation/types/navigation';
import { ArrowLeft } from '@phosphor-icons/react';
import { clsx } from 'clsx';
import FocusLock, { useFocusScope } from 'react-focus-lock';

interface Props {
  data: Navigation[];
  parentId: number;
  parentTitle: string;
  href?: string;
}

const selector = ({
  mobileMenuTopSectionHeight,
  setNavigationPath,
  goBack,
  navigationPath,
}: NavigationStore) => ({
  mobileMenuTopSectionHeight,
  setNavigationPath,
  goBack,
  navigationPath,
});

export const Menu = ({ data, parentId, href, parentTitle }: Props) => {
  const {
    mobileMenuTopSectionHeight,
    setNavigationPath,
    goBack,
    navigationPath,
  } = useNavigationStore(selector);

  const id = navigationPath.at(-1);
  const isCurrentIdActive = id === parentId;
  const isCurrentCategory = navigationPath.includes(parentId);

  const { autoFocus } = useFocusScope();

  const onClick = (id: number) => {
    setNavigationPath(id);
  };

  const ref = (node: HTMLElement | null) => {
    if (!node) return;
    node.ontransitionend = (event: TransitionEvent) => {
      if (event.propertyName === 'transform' && isCurrentIdActive) {
        autoFocus();
      }
    };
  };

  return (
    <div
      className={clsx(
        'invisible absolute top-0 left-0 z-30 s:mt-s flex h-dvh w-full flex-col overflow-hidden bg-main pt-m pb-l transition-transform duration-300 ease-in-out',
      )}
      style={{
        //48px = default height, 6px is brand-top-section height and 3px is padding for outline
        paddingTop: `${(mobileMenuTopSectionHeight ?? 48) + 6 + 3}px`,
        transform: isCurrentCategory ? 'translateX(0%)' : 'translateX(100%)',
        visibility: isCurrentCategory ? 'visible' : 'hidden',
        transition: isCurrentCategory
          ? 'transform 300ms ease-in-out, visibility 0ms linear'
          : 'transform 300ms ease-in-out, visibility 0ms linear 300ms',
      }}
      ref={ref}
      data-isopen={isCurrentIdActive}
      data-testid={'mobile-menu-list'}
    >
      <FocusLock
        group="mobile-menu"
        className="flex h-full flex-col gap-m pt-3"
        disabled={!isCurrentIdActive}
        autoFocus={false}
      >
        <Button
          variant="tertiary-no-frame"
          onClick={goBack}
          className="!max-w-full !pl-0 s:mx-s xs:mx-xxs h-clickable-large"
          data-entire-category-id={parentId}
          data-testid={'mobile-menu-back-button'}
        >
          <div className="flex w-full items-center justify-start">
            <div className="grid h-clickable-large w-clickable-large place-items-center">
              <PhosphorIcon
                Icon={ArrowLeft}
                variant="regular"
                className="icon-l mr-[15px]"
              />
            </div>
            <span className="font-semibold text-heading-l text-strong">
              {parentTitle}
            </span>
          </div>
        </Button>
        <ul
          className="flex h-full flex-col overflow-x-hidden s:px-s xs:px-xxs py-3"
          hidden={!isCurrentCategory || !isCurrentIdActive}
        >
          {href && (
            <>
              <MobileNavigationLink
                key={id}
                href={href}
                id={-1}
                onClick={onClick}
                redirectToEntireCategory
              />
              <div className="p-xs ">
                <Divider />
              </div>
            </>
          )}
          {data?.map(({ title, id, children, href }) => (
            <MobileNavigationLink
              title={title}
              key={id}
              href={href}
              id={id}
              onClick={onClick}
              redirectToEntireCategory={false}
              nodes={children}
            />
          ))}
        </ul>
      </FocusLock>
    </div>
  );
};
